/**
 * apps/battle/client/src/terminal/screens/VerifyEmailSentScreen.tsx
 *
 * Phase 4.2 M7 — verify-email "check your inbox" screen.
 *
 * Shown after signup (auto-login already happened) and from the Account screen
 * via the "Resend verification email" action. Under M12b, email verification is
 * REQUIRED for gameplay: content routes 403 until the email is confirmed, so the
 * student is gated by VerifyGateScreen until they verify.
 */
import React, { useState } from 'react';
import { api, ApiError } from '../../api.js';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';
import { Spinner } from '../../widgets.js';

interface VerifyEmailSentScreenProps {
  email: string;
  /** Continue to the game (title screen). */
  onContinue: () => void;
}

export function VerifyEmailSentScreen({ email, onContinue }: VerifyEmailSentScreenProps) {
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  async function resend() {
    setStatus('sending');
    setErrMsg(null);
    try {
      await api.post('/api/auth/request-verify', {});
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      if (err instanceof ApiError && err.status === 429) {
        setErrMsg('Please wait a few minutes before requesting another email.');
      } else {
        setErrMsg('Could not resend verification email. Please try again later.');
      }
    }
  }

  const keybar: KeyBarItem[] = [
    { label: 'Enter', description: 'continue to game', onActivate: onContinue },
    { label: 'R', description: 'resend verification', onActivate: resend, disabled: status === 'sending' },
  ];

  return (
    <TerminalFrame
      title={<>ECONQUEST &nbsp;&nbsp;─&nbsp;&nbsp;VERIFY YOUR EMAIL</>}
      keybar={<KeyBar items={keybar} />}
    >
      <div style={{ maxWidth: 520, margin: '0 auto', paddingTop: 24, textAlign: 'left' }}>
        <h2 style={{ color: '#e6ebf2', fontSize: 18, marginTop: 0 }}>Check your inbox</h2>
        <p style={{ color: '#cdd5e0', lineHeight: 1.6 }}>
          We've sent a verification link to <strong style={{ color: '#e6ebf2' }}>{email}</strong>.
          The link expires in 24 hours.
        </p>
        <p style={{ color: '#97a3b6', lineHeight: 1.6, fontSize: 13 }}>
          You'll need to verify your email before you can start playing — click the
          link in that email, then continue.
        </p>

        {status === 'sent' && (
          <div style={{ marginTop: 12, padding: 12, background: '#0f2a1c', border: '1px solid #1f6d3e', borderRadius: 6, color: '#a7e8c5', fontSize: 13 }}>
            Verification email resent.
          </div>
        )}
        {status === 'error' && errMsg && (
          <div style={{ marginTop: 12, padding: 12, background: '#2a0f0f', border: '1px solid #6d1f1f', borderRadius: 6, color: '#e8a7a7', fontSize: 13 }}>
            {errMsg}
          </div>
        )}

        <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
          <button className="btn btn-primary" type="button" onClick={onContinue}>
            Continue to game
          </button>
          <button className="btn" type="button" onClick={resend} disabled={status === 'sending'}>
            {status === 'sending' ? <Spinner size={14} /> : 'Resend email'}
          </button>
        </div>
      </div>
    </TerminalFrame>
  );
}
